import React, { useState, useEffect } from 'react';
import { 
  categorySlaApi, 
  categoryManagementApi,
  CategoryWithSLA,
  SupportCategory,
  CreateCategoryRequest
} from '../services/supportApi';
import { FiClock, FiEdit2, FiSave, FiX, FiRefreshCw, FiChevronDown, FiChevronRight, FiAlertCircle, FiPlus, FiTrash2 } from 'react-icons/fi';
import './SLAManagement.css';

const SLAManagement: React.FC = () => {
  const [categories, setCategories] = useState<CategoryWithSLA[]>([]);
  const [allCategories, setAllCategories] = useState<SupportCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [editingCategory, setEditingCategory] = useState<number | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [creatingForParent, setCreatingForParent] = useState<number | null>(null);
  const [categoryFormData, setCategoryFormData] = useState({
    slaOverrideEnabled: false,
    slaResponseHoursOverride: 0,
    slaResolutionHoursOverride: 0
  });
  const [newCategoryForm, setNewCategoryForm] = useState<CreateCategoryRequest>({
    nameAr: '',
    nameEn: '',
    defaultPriority: 'MEDIUM',
    defaultCriticality: 'MEDIUM',
    parentId: null,
    displayOrder: 0,
    description: '',
    icon: '',
    slaOverrideEnabled: false,
    slaResponseHoursOverride: undefined,
    slaResolutionHoursOverride: undefined
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      // Load both hierarchy view and all categories for management
      const [hierarchyData, allCategoriesData] = await Promise.all([
        categorySlaApi.getHierarchy(),
        categoryManagementApi.getHierarchy()
      ]);
      setCategories(hierarchyData);
      setAllCategories(allCategoriesData);
    } catch (error: any) {
      console.error('Failed to load data:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingCategory(null);
    setShowCreateForm(false);
    setCreatingForParent(null);
    setNewCategoryForm({
      nameAr: '',
      nameEn: '',
      defaultPriority: 'MEDIUM',
      defaultCriticality: 'MEDIUM',
      parentId: null,
      displayOrder: 0,
      description: '',
      icon: '',
      slaOverrideEnabled: false,
      slaResponseHoursOverride: undefined,
      slaResolutionHoursOverride: undefined
    });
  };

  const handleCreateCategory = async () => {
    try {
      if (!newCategoryForm.nameAr || !newCategoryForm.nameEn) {
        alert('Please fill in both Arabic and English names');
        return;
      }
      
      const categoryData: CreateCategoryRequest = {
        ...newCategoryForm,
        parentId: creatingForParent || null
      };
      
      await categoryManagementApi.create(categoryData);
      await loadData();
      handleCancelEdit();
      alert('Category created successfully!');
    } catch (error: any) {
      console.error('Failed to create category:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleDeleteCategory = async (id: number, name: string) => {
    if (!window.confirm(`Are you sure you want to delete "${name}"? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await categoryManagementApi.delete(id);
      await loadData();
      alert('Category deleted successfully!');
    } catch (error: any) {
      console.error('Failed to delete category:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleAddSubcategory = (parentId: number) => {
    setCreatingForParent(parentId);
    setNewCategoryForm({
      ...newCategoryForm,
      parentId: parentId
    });
    setShowCreateForm(true);
    // Expand parent to show the form
    setExpandedCategories(prev => new Set(prev).add(parentId));
  };

  const handleEditCategory = async (categoryId: number) => {
    try {
      const slaData = await categoryManagementApi.getSLA(categoryId);
      setEditingCategory(categoryId);
      setCategoryFormData({
        slaOverrideEnabled: slaData.slaOverrideEnabled,
        slaResponseHoursOverride: slaData.slaResponseHoursOverride || 0,
        slaResolutionHoursOverride: slaData.slaResolutionHoursOverride || 0
      });
    } catch (error: any) {
      console.error('Failed to load category SLA:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const handleSaveCategory = async (id: number) => {
    try {
      await categoryManagementApi.updateSLA(id, categoryFormData);
      setEditingCategory(null);
      await loadData();
      alert('Category SLA updated successfully!');
    } catch (error: any) {
      console.error('Failed to update category SLA:', error);
      alert(`Error: ${error.response?.data?.message || error.message}`);
    }
  };

  const toggleCategory = (categoryId: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(categoryId)) {
      newExpanded.delete(categoryId);
    } else {
      newExpanded.add(categoryId);
    }
    setExpandedCategories(newExpanded);
  };

  const formatHours = (hours: number): string => {
    if (hours < 1) return `${Math.round(hours * 60)} minutes`;
    if (hours < 24) return `${hours} hour${hours !== 1 ? 's' : ''}`;
    const days = Math.floor(hours / 24);
    const remainingHours = hours % 24;
    if (remainingHours === 0) return `${days} day${days !== 1 ? 's' : ''}`;
    return `${days} day${days !== 1 ? 's' : ''} ${remainingHours} hour${remainingHours !== 1 ? 's' : ''}`;
  };

  const renderCategory = (category: CategoryWithSLA, level: number = 0) => {
    const isExpanded = expandedCategories.has(category.id);
    const isEditing = editingCategory === category.id;
    const hasChildren = category.children && category.children.length > 0;
    const isCreatingSubcategory = showCreateForm && creatingForParent === category.id;

    return (
      <div key={category.id} className="category-item" style={{ marginLeft: `${level * 30}px` }}>
        <div className="category-header">
          <div className="category-info">
            {(hasChildren || level === 0) && (
              <button 
                className="expand-button"
                onClick={() => toggleCategory(category.id)}
              >
                {React.createElement(isExpanded ? FiChevronDown : FiChevronRight as any)}
              </button>
            )}
            {!hasChildren && level > 0 && <span className="expand-spacer" />}
            <div className="category-details">
              <h4>{category.nameEn}</h4>
              <p dir="rtl">{category.nameAr}</p>
              {category.description && <p className="category-description">{category.description}</p>}
            </div>
          </div>
          <div className="category-sla-info">
            {isEditing ? (
              <div className="sla-edit-form">
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={categoryFormData.slaOverrideEnabled}
                      onChange={(e) => setCategoryFormData({ ...categoryFormData, slaOverrideEnabled: e.target.checked })}
                    />
                    Enable SLA Override
                  </label>
                </div>
                {categoryFormData.slaOverrideEnabled && (
                  <>
                    <div className="form-group">
                      <label>Response Time (hours)</label>
                      <input
                        type="number"
                        min="1"
                        max="168"
                        value={categoryFormData.slaResponseHoursOverride}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, slaResponseHoursOverride: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Resolution Time (hours)</label>
                      <input
                        type="number"
                        min="1"
                        max="720"
                        value={categoryFormData.slaResolutionHoursOverride}
                        onChange={(e) => setCategoryFormData({ ...categoryFormData, slaResolutionHoursOverride: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </>
                )}
                <div className="form-actions">
                  <button className="btn-save" onClick={() => handleSaveCategory(category.id)}>
                    {React.createElement(FiSave as any)} Save
                  </button>
                  <button className="btn-cancel" onClick={handleCancelEdit}>
                    {React.createElement(FiX as any)} Cancel
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="sla-display">
                  <div className="sla-item">
                    <span className="sla-label">Response:</span>
                    <span className="sla-value">{category.slaResponseFormatted || formatHours(category.slaResponseHours)}</span>
                  </div>
                  <div className="sla-item">
                    <span className="sla-label">Resolution:</span>
                    <span className="sla-value">{category.slaResolutionFormatted || formatHours(category.slaResolutionHours)}</span>
                  </div>
                  {category.slaOverrideEnabled && (
                    <span className="override-badge">Custom SLA</span>
                  )}
                  {!category.slaOverrideEnabled && (
                    <span className="default-badge">Default Priority SLA</span>
                  )}
                </div>
                <div className="category-actions">
                  <button className="btn-edit" onClick={() => handleEditCategory(category.id)}>
                    {React.createElement(FiEdit2 as any)} Edit SLA
                  </button>
                  <button className="btn-add-sub" onClick={() => handleAddSubcategory(category.id)}>
                    {React.createElement(FiPlus as any)} Add Subcategory
                  </button>
                  <button className="btn-delete" onClick={() => handleDeleteCategory(category.id, category.nameEn)}>
                    {React.createElement(FiTrash2 as any)} Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
        {isExpanded && (
          <div className="category-children">
            {isCreatingSubcategory && (
              <div className="create-category-form" style={{ marginLeft: '30px' }}>
                <h4>Create New Subcategory</h4>
                <div className="form-group">
                  <label>Name (English) *</label>
                  <input
                    type="text"
                    value={newCategoryForm.nameEn}
                    onChange={(e) => setNewCategoryForm({ ...newCategoryForm, nameEn: e.target.value })}
                    placeholder="e.g., Refinance"
                  />
                </div>
                <div className="form-group">
                  <label>Name (Arabic) *</label>
                  <input
                    type="text"
                    value={newCategoryForm.nameAr}
                    onChange={(e) => setNewCategoryForm({ ...newCategoryForm, nameAr: e.target.value })}
                    placeholder="e.g., إعادة التمويل"
                    dir="rtl"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    value={newCategoryForm.description || ''}
                    onChange={(e) => setNewCategoryForm({ ...newCategoryForm, description: e.target.value })}
                    placeholder="Category description"
                    rows={3}
                  />
                </div>
                <div className="form-group">
                  <label>Default Priority</label>
                  <select
                    value={newCategoryForm.defaultPriority}
                    onChange={(e) => setNewCategoryForm({ ...newCategoryForm, defaultPriority: e.target.value })}
                  >
                    <option value="LOW">LOW</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="HIGH">HIGH</option>
                    <option value="URGENT">URGENT</option>
                    <option value="CRITICAL">CRITICAL</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Default Criticality</label>
                  <select
                    value={newCategoryForm.defaultCriticality || 'MEDIUM'}
                    onChange={(e) => setNewCategoryForm({ ...newCategoryForm, defaultCriticality: e.target.value })}
                  >
                    <option value="LOW">LOW</option>
                    <option value="MEDIUM">MEDIUM</option>
                    <option value="HIGH">HIGH</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newCategoryForm.slaOverrideEnabled || false}
                      onChange={(e) => setNewCategoryForm({ ...newCategoryForm, slaOverrideEnabled: e.target.checked })}
                    />
                    Enable SLA Override
                  </label>
                </div>
                {newCategoryForm.slaOverrideEnabled && (
                  <>
                    <div className="form-group">
                      <label>Response Time Override (hours)</label>
                      <input
                        type="number"
                        min="1"
                        max="168"
                        value={newCategoryForm.slaResponseHoursOverride || ''}
                        onChange={(e) => setNewCategoryForm({ ...newCategoryForm, slaResponseHoursOverride: parseInt(e.target.value) || undefined })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Resolution Time Override (hours)</label>
                      <input
                        type="number"
                        min="1"
                        max="720"
                        value={newCategoryForm.slaResolutionHoursOverride || ''}
                        onChange={(e) => setNewCategoryForm({ ...newCategoryForm, slaResolutionHoursOverride: parseInt(e.target.value) || undefined })}
                      />
                    </div>
                  </>
                )}
                <div className="form-actions">
                  <button className="btn-save" onClick={handleCreateCategory}>
                    {React.createElement(FiSave as any)} Create
                  </button>
                  <button className="btn-cancel" onClick={handleCancelEdit}>
                    {React.createElement(FiX as any)} Cancel
                  </button>
                </div>
              </div>
            )}
            {hasChildren && category.children?.map(child => renderCategory(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="sla-management">
      <div className="sla-header">
        <h2>
          {React.createElement(FiClock as any, { className: "header-icon" })}
          SLA Management
        </h2>
        <button className="btn-refresh" onClick={loadData} disabled={loading}>
          {React.createElement(FiRefreshCw as any, { className: loading ? 'spinning' : '' })}
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      ) : (
        <div className="categories-list">
          <div className="info-banner">
            {React.createElement(FiAlertCircle as any)}
            <p>
              <strong>Note:</strong> SLA is calculated from category/subcategory settings, NOT from subscription plans.
              If a category has SLA override enabled, it uses custom SLA times. Otherwise, it uses the priority's default SLA.
            </p>
          </div>
          <div className="categories-header">
            <button className="btn-add-category" onClick={() => {
              setCreatingForParent(null);
              setNewCategoryForm({ ...newCategoryForm, parentId: null });
              setShowCreateForm(true);
            }}>
              {React.createElement(FiPlus as any)} Add New Category
            </button>
          </div>
          {showCreateForm && !creatingForParent && (
            <div className="create-category-form">
              <h4>Create New Category</h4>
              <div className="form-group">
                <label>Name (English) *</label>
                <input
                  type="text"
                  value={newCategoryForm.nameEn}
                  onChange={(e) => setNewCategoryForm({ ...newCategoryForm, nameEn: e.target.value })}
                  placeholder="e.g., Technical Support"
                />
              </div>
              <div className="form-group">
                <label>Name (Arabic) *</label>
                <input
                  type="text"
                  value={newCategoryForm.nameAr}
                  onChange={(e) => setNewCategoryForm({ ...newCategoryForm, nameAr: e.target.value })}
                  placeholder="e.g., الدعم التقني"
                  dir="rtl"
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newCategoryForm.description || ''}
                  onChange={(e) => setNewCategoryForm({ ...newCategoryForm, description: e.target.value })}
                  placeholder="Category description"
                  rows={3}
                />
              </div>
              <div className="form-group">
                <label>Default Priority</label>
                <select
                  value={newCategoryForm.defaultPriority}
                  onChange={(e) => setNewCategoryForm({ ...newCategoryForm, defaultPriority: e.target.value })}
                >
                  <option value="LOW">LOW</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="HIGH">HIGH</option>
                  <option value="URGENT">URGENT</option>
                  <option value="CRITICAL">CRITICAL</option>
                </select>
              </div>
              <div className="form-group">
                <label>Default Criticality</label>
                <select
                  value={newCategoryForm.defaultCriticality || 'MEDIUM'}
                  onChange={(e) => setNewCategoryForm({ ...newCategoryForm, defaultCriticality: e.target.value })}
                >
                  <option value="LOW">LOW</option>
                  <option value="MEDIUM">MEDIUM</option>
                  <option value="HIGH">HIGH</option>
                </select>
              </div>
              <div className="form-group">
                <label>
                  <input
                    type="checkbox"
                    checked={newCategoryForm.slaOverrideEnabled || false}
                    onChange={(e) => setNewCategoryForm({ ...newCategoryForm, slaOverrideEnabled: e.target.checked })}
                  />
                  Enable SLA Override
                </label>
              </div>
              {newCategoryForm.slaOverrideEnabled && (
                <>
                  <div className="form-group">
                    <label>Response Time Override (hours)</label>
                    <input
                      type="number"
                      min="1"
                      max="168"
                      value={newCategoryForm.slaResponseHoursOverride || ''}
                      onChange={(e) => setNewCategoryForm({ ...newCategoryForm, slaResponseHoursOverride: parseInt(e.target.value) || undefined })}
                    />
                  </div>
                  <div className="form-group">
                    <label>Resolution Time Override (hours)</label>
                    <input
                      type="number"
                      min="1"
                      max="720"
                      value={newCategoryForm.slaResolutionHoursOverride || ''}
                      onChange={(e) => setNewCategoryForm({ ...newCategoryForm, slaResolutionHoursOverride: parseInt(e.target.value) || undefined })}
                    />
                  </div>
                </>
              )}
              <div className="form-actions">
                <button className="btn-save" onClick={handleCreateCategory}>
                  {React.createElement(FiSave as any)} Create
                </button>
                <button className="btn-cancel" onClick={handleCancelEdit}>
                  {React.createElement(FiX as any)} Cancel
                </button>
              </div>
            </div>
          )}
          {categories.map(category => renderCategory(category))}
        </div>
      )}
    </div>
  );
};

export default SLAManagement;

